// src/pages/WebDevelopment.js
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Code2,
  Wrench,
  Smartphone,
  Zap,
  CheckCircle,
  Mail,
  MessageSquare,
  Globe,
  Star,
  ShoppingCart,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "../utils/createPageUrl";
import "../styles/webDevelopment.css";
import Coworking from "../assets/images/coding-workspace.jpeg";
import Screen from "../assets/images/screen.jpeg";

// ✅ Helper multi-catégories (comme dans Portfolio)
const getProjectCategoryIds = (project) => {
  if (Array.isArray(project.categoryIds) && project.categoryIds.length > 0) {
    return project.categoryIds;
  }
  if (project.categoryId) {
    return [project.categoryId];
  }
  return [];
};

function WebDevelopment() {
  const { t } = useTranslation();
  const location = useLocation();

  // FORM STATES
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    projectType: "",
    budget: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Smooth scroll when landing on hash (#webdev-contact)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // DATA FROM TRANSLATION FILES
  const services = t("webDev.services.items", { returnObjects: true }) || [];
  const valueItems = t("webDev.value.items", { returnObjects: true }) || [];
  const projects =
    t("webDev.portfolio.projects", { returnObjects: true }) || [];
  const testimonials =
    t("webDev.testimonials.items", { returnObjects: true }) || [];

  const projectTypeOptions = t(
    "webDev.contact.fields.projectTypeOptions",
    { returnObjects: true }
  );
  const budgetOptions = t(
    "webDev.contact.fields.budgetOptions",
    { returnObjects: true }
  );

  // FORM SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formPayload.append(key, value);
      });
      formPayload.append("formContext", "webdev");

      const response = await fetch("/contact.php", {
        method: "POST",
        body: formPayload,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        setErrorMessage(t("webDev.contact.errorMessage"));
        setIsSubmitted(false);
      } else {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          company: "",
          projectType: "",
          budget: "",
          message: "",
        });
      }
    } catch (err) {
      setErrorMessage(t("webDev.contact.errorMessage"));
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // SERVICE ICONS
  const renderServiceIcon = (iconKey) => {
    switch (iconKey) {
      case "globe":
        return <Globe className="webdev__service-icon-svg" />;
      case "smartphone":
        return <Smartphone className="webdev__service-icon-svg" />;
      case "cart":
        return <ShoppingCart className="webdev__service-icon-svg" />;
      case "zap":
        return <Zap className="webdev__service-icon-svg" />;
      case "code":
        return <Code2 className="webdev__service-icon-svg" />;
      case "message":
        return <Wrench className="webdev__service-icon-svg" />;
      default:
        return <Code2 className="webdev__service-icon-svg" />;
    }
  };

  return (
    <div className="webdev">
      {/* HERO */}
      <section className="webdev__hero">
        <div className="webdev__hero-bg">
          <img
            src={Coworking}
            alt="Coding workspace"
            className="webdev__hero-bg-image"
          />
          <div className="webdev__hero-bg-overlay" />

          {/* Floating code symbols */}
          <div className="webdev__hero-code-layer">
            <motion.div
              className="webdev__hero-code-symbol webdev__hero-code-symbol--one"
              animate={{ y: [0, 20, 0], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 6, repeat: Infinity }}
            >
              {"</>"}
            </motion.div>

            <motion.div
              className="webdev__hero-code-symbol webdev__hero-code-symbol--two"
              animate={{ y: [0, -25, 0], opacity: [0.1, 0.15, 0.1] }}
              transition={{ duration: 8, repeat: Infinity }}
            >
              {"{ }"}
            </motion.div>

            <motion.div
              className="webdev__hero-code-symbol webdev__hero-code-symbol--three"
              animate={{ x: [0, 15, 0], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 7, repeat: Infinity }}
            >
              {"()"}
            </motion.div>
          </div>
        </div>

        <div className="webdev__hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="webdev__hero-badge"
          >
            <Code2 className="webdev__hero-badge-icon" />
            <span className="webdev__hero-badge-text">
              {t("webDev.hero.badge")}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="webdev__hero-title"
          >
            {t("webDev.hero.titleLine1")}
            <br />
            <span className="webdev__hero-title-highlight">
              {t("webDev.hero.titleHighlight")}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="webdev__hero-subtitle"
          >
            {t("webDev.hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              type="button"
              className="webdev__hero-cta"
              onClick={() => {
                const el = document.getElementById("webdev-contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              {t("webDev.hero.cta")}
            </button>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="webdev__section webdev__section--services">
        <div className="webdev__section-inner">
          <motion.div
            className="webdev__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="webdev__section-title">
              {t("webDev.services.sectionTitle")}
            </h2>
            <p className="webdev__section-subtitle">
              {t("webDev.services.sectionSubtitle")}
            </p>
          </motion.div>

          <div className="webdev__services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="webdev__service-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -5 }}
              >
                <div className="webdev__service-icon-wrapper">
                  {renderServiceIcon(service.icon)}
                </div>

                <h3 className="webdev__service-title">{service.title}</h3>
                <p className="webdev__service-text">
                  {service.description}
                </p>

                <ul className="webdev__service-list">
                  {service.features?.map((feature) => (
                    <li
                      key={feature}
                      className="webdev__service-list-item"
                    >
                      <CheckCircle className="webdev__service-list-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE */}
      <section className="webdev__section webdev__section--value">
        <div className="webdev__value-bg">
          <svg width="100%" height="100%">
            <defs>
              <pattern
                id="webdev-circuit"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="2" fill="#2D8CFF" />
                <circle cx="55" cy="55" r="2" fill="#2D8CFF" />
                <path
                  d="M 5 5 L 55 5 L 55 55"
                  fill="none"
                  stroke="#2D8CFF"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#webdev-circuit)"
            />
          </svg>
        </div>

        <div className="webdev__section-inner webdev__section-inner--value">
          <motion.div
            className="webdev__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="webdev__section-title">
              {t("webDev.value.sectionTitle")}
            </h2>
          </motion.div>

          <div className="webdev__value-grid">
            {valueItems.map((item, index) => (
              <motion.div
                key={item.title}
                className="webdev__value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="webdev__value-stat">
                  {item.stat}
                </div>
                <div className="webdev__value-stat-label">
                  {item.statLabel}
                </div>
                <h3 className="webdev__value-title">{item.title}</h3>
                <p className="webdev__value-text">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section className="webdev__section webdev__section--portfolio">
        <div className="webdev__section-inner">
          <motion.div
            className="webdev__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="webdev__section-title">
              {t("webDev.portfolio.sectionTitle")}
            </h2>
            <p className="webdev__section-subtitle">
              {t("webDev.portfolio.sectionSubtitle")}
            </p>
          </motion.div>

          <div className="webdev__projects-grid">
            {projects.map((project, index) => {
              const categoryIds = getProjectCategoryIds(project);

              return (
                <motion.a
                  key={project.title}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="webdev__project-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -4 }}
                >
                  <div className="webdev__project-image-wrapper">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="webdev__project-image"
                    />
                    <div className="webdev__project-image-overlay" />

                    {/* ✅ Plusieurs pastilles en colonne */}
                    <div className="webdev__project-categories">
                      {categoryIds.map((catId) => (
                        <span
                          key={catId}
                          className="webdev__project-category"
                          style={{
                            backgroundColor:
                              project.color || "#2D8CFF",
                          }}
                        >
                          {catId}
                        </span>
                      ))}
                    </div>

                    <div className="webdev__project-hover">
                      <ExternalLink className="webdev__project-hover-icon" />
                    </div>
                  </div>

                  <div className="webdev__project-body">
                    <h3 className="webdev__project-title">
                      {project.title}
                    </h3>
                    <p className="webdev__project-text">
                      {project.description}
                    </p>
                    <div className="webdev__project-tags">
                      {project.tech?.map((tech) => (
                        <span
                          key={tech}
                          className="webdev__project-tag"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </div>

          <motion.div
            className="webdev__portfolio-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <Link to={createPageUrl("Portfolio")}>
              <button className="webdev__portfolio-cta-btn">
                {t("webDev.portfolio.ctaButton")}
                <ExternalLink className="webdev__portfolio-cta-icon" />
              </button>
            </Link>
            <p className="webdev__portfolio-cta-subtitle">
              {t("webDev.portfolio.ctaSubtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="webdev__section webdev__section--testimonials">
        <div className="webdev__testimonials-bg">
          <img
            src={Screen}
            alt="Tech workspace"
            className="webdev__testimonials-bg-image"
          />
        </div>

        <div className="webdev__section-inner webdev__section-inner--testimonials">
          <motion.div
            className="webdev__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="webdev__section-title webdev__testimonials-title">
              {t("webDev.testimonials.sectionTitle")}
            </h2>
          </motion.div>

          <div className="webdev__testimonials-grid">
            {testimonials.map((item, index) => (
              <motion.div
                key={item.name}
                className="webdev__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="webdev__testimonial-stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className="webdev__testimonial-star"
                    />
                  ))}
                </div>

                <p className="webdev__testimonial-text">
                  “{item.text}”
                </p>

                <div className="webdev__testimonial-author">
                  <p className="webdev__testimonial-name">
                    {item.name}
                  </p>
                  <p className="webdev__testimonial-role">
                    {item.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section
        id="webdev-contact"
        className="webdev__section webdev__section--contact"
      >
        <div className="webdev__section-inner webdev__section-inner--contact">
          <motion.div
            className="webdev__contact-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="webdev__contact-header">
              <div className="webdev__contact-header-overlay" />
              <div className="webdev__contact-header-content">
                <h2 className="webdev__contact-title">
                  {t("webDev.contact.headerTitle")}
                </h2>
                <p className="webdev__contact-subtitle">
                  {t("webDev.contact.headerSubtitle")}
                </p>
              </div>
            </div>

            <div className="webdev__contact-body">
              {isSubmitted ? (
                <motion.div
                  className="webdev__contact-success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="webdev__contact-success-icon-wrapper">
                    <CheckCircle className="webdev__contact-success-icon" />
                  </div>
                  <h3 className="webdev__contact-success-title">
                    {t("webDev.contact.successTitle")}
                  </h3>
                  <p className="webdev__contact-success-text">
                    {t("webDev.contact.successText")}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="webdev__form">
                  <div className="webdev__form-row">
                    <div className="webdev__form-field">
                      <label className="webdev__form-label">
                        {t("webDev.contact.fields.nameLabel")}
                      </label>
                      <input
                        type="text"
                        required
                        className="webdev__input"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "webDev.contact.fields.namePlaceholder"
                        )}
                      />
                    </div>

                    <div className="webdev__form-field">
                      <label className="webdev__form-label">
                        {t("webDev.contact.fields.emailLabel")}
                      </label>
                      <input
                        type="email"
                        required
                        className="webdev__input"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "webDev.contact.fields.emailPlaceholder"
                        )}
                      />
                    </div>
                  </div>

                  <div className="webdev__form-row">
                    <div className="webdev__form-field">
                      <label className="webdev__form-label">
                        {t("webDev.contact.fields.companyLabel")}
                      </label>
                      <input
                        type="text"
                        className="webdev__input"
                        value={formData.company}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            company: e.target.value,
                          }))
                        }
                        placeholder={t(
                          "webDev.contact.fields.companyPlaceholder"
                        )}
                      />
                    </div>

                    <div className="webdev__form-field">
                      <label className="webdev__form-label">
                        {t("webDev.contact.fields.projectTypeLabel")}
                      </label>
                      <select
                        className="webdev__select"
                        value={formData.projectType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            projectType: e.target.value,
                          }))
                        }
                      >
                        <option value="">
                          {t(
                            "webDev.contact.fields.projectTypePlaceholder"
                          )}
                        </option>
                        <option value="website">
                          {projectTypeOptions.website}
                        </option>
                        <option value="webapp">
                          {projectTypeOptions.webapp}
                        </option>
                        <option value="ecommerce">
                          {projectTypeOptions.ecommerce}
                        </option>
                        <option value="other">
                          {projectTypeOptions.other}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="webdev__form-field">
                    <label className="webdev__form-label">
                      {t("webDev.contact.fields.budgetLabel")}
                    </label>
                    <select
                      className="webdev__select"
                      value={formData.budget}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          budget: e.target.value,
                        }))
                      }
                    >
                      <option value="">
                        {t(
                          "webDev.contact.fields.budgetPlaceholder"
                        )}
                      </option>
                      <option value="0-500">
                        {budgetOptions["0-500"]}
                      </option>
                      <option value="500-1k">
                        {budgetOptions["500-1k"]}
                      </option>
                      <option value="1k-5k">
                        {budgetOptions["1k-5k"]}
                      </option>
                      <option value="5k-10k">
                        {budgetOptions["5k-10k"]}
                      </option>
                    </select>
                  </div>

                  <div className="webdev__form-field">
                    <label className="webdev__form-label">
                      {t("webDev.contact.fields.messageLabel")}
                    </label>
                    <textarea
                      required
                      className="webdev__textarea"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder={t(
                        "webDev.contact.fields.messagePlaceholder"
                      )}
                    />
                  </div>

                  {errorMessage && (
                    <p className="webdev__form-error">
                      {errorMessage}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="webdev__submit"
                  >
                    {isSubmitting
                      ? t("webDev.contact.submittingLabel")
                      : t("webDev.contact.submitLabel")}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          <motion.div
            className="webdev__contact-info"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="webdev__contact-info-item">
              <div className="webdev__contact-info-icon-wrapper">
                <Mail className="webdev__contact-info-icon" />
              </div>
              <div>
                <p className="webdev__contact-info-label">
                  {t("webDev.contact.info.emailLabel")}
                </p>
                <p className="webdev__contact-info-value">
                  {t("webDev.contact.info.emailValue")}
                </p>
              </div>
            </div>

            <div className="webdev__contact-info-item">
              <div className="webdev__contact-info-icon-wrapper">
                <MessageSquare className="webdev__contact-info-icon" />
              </div>
              <div>
                <p className="webdev__contact-info-label">
                  {t("webDev.contact.info.responseLabel")}
                </p>
                <p className="webdev__contact-info-value">
                  {t("webDev.contact.info.responseValue")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default WebDevelopment;
